/**
 * LocalStorage-based workspace operations (auto-save/restore)
 */

import logger from "../../utils/logger";
import { WORKSPACE_CONFIG, getAutoSaveKey } from "../constants.js";
import {
  createWorkspaceSnapshot,
  deserializeWorkspace,
} from "../core/serialization.js";

/**
 * Auto-save workspace to localStorage (synchronous for beforeunload)
 * Uses user-scoped keys to prevent workspace leakage between users/brokers
 * @param {Object} chartsStore - The charts store state
 * @param {string} user - Username/email for scoping
 * @param {string} broker - Broker identifier for scoping
 * @returns {boolean} - Success status
 */
export const autoSaveWorkspace = (chartsStore, user, broker) => {
  try {
    const { charts } = chartsStore;

    // Don't save if no charts
    if (!charts || charts.length === 0) {
      console.log("Skipping auto-save: no charts to save");
      return false;
    }

    // Create a simplified workspace snapshot (synchronous, no await)
    const workspaceSnapshot = createWorkspaceSnapshot(chartsStore, {
      simplified: true,
      metadata: {
        app: WORKSPACE_CONFIG.APP_NAME,
        description: "Auto-saved desktop snapshot",
        autoSave: true,
        user, // Track who saved this workspace
        broker, // Track which broker context
      },
    });

    // Get user-scoped key
    const autoSaveKey = getAutoSaveKey(user, broker);

    // Save to localStorage with user-scoped key
    localStorage.setItem(autoSaveKey, JSON.stringify(workspaceSnapshot));

    console.log(`Desktop auto-saved to localStorage [${autoSaveKey}]`);
    return true;
  } catch (error) {
    console.error("Failed to auto-save desktop:", error);
    return false;
  }
};

/**
 * Check if there's an auto-saved workspace available for the current user/broker
 * @param {string} user - Username/email for scoping
 * @param {string} broker - Broker identifier for scoping
 * @returns {Object|null} - Auto-saved workspace data or null if not found
 */
export const getAutoSavedWorkspace = (user, broker) => {
  try {
    const autoSaveKey = getAutoSaveKey(user, broker);
    const autoSavedData = localStorage.getItem(autoSaveKey);

    if (autoSavedData) {
      const parsed = JSON.parse(autoSavedData);
      // Validate that it's a valid workspace
      if (parsed.version && parsed.charts && Array.isArray(parsed.charts)) {
        return parsed;
      }
    }
    return null;
  } catch (error) {
    console.error("Failed to retrieve auto-saved desktop:", error);
    return null;
  }
};

/**
 * Load auto-saved workspace from localStorage
 * @param {Function} loadCharts - Function to load charts into store
 * @param {string} user - Username/email for scoping
 * @param {string} broker - Broker identifier for scoping
 * @returns {boolean} - Success status
 */
export const loadAutoSavedWorkspace = (loadCharts, user, broker) => {
  try {
    const autoSavedData = getAutoSavedWorkspace(user, broker);

    if (
      !autoSavedData ||
      !Array.isArray(autoSavedData.charts) ||
      autoSavedData.charts.length === 0
    ) {
      console.log("No auto-saved desktop found");
      return false;
    }

    console.log("Loading auto-saved desktop from:", autoSavedData.timestamp);

    // Use the existing deserializeWorkspace function to load the data
    const success = deserializeWorkspace(autoSavedData, loadCharts);

    // Success logged in console only - UI logger message removed to reduce noise
    return success;
  } catch (error) {
    console.error("Failed to load auto-saved desktop:", error);
    logger.workspace.error("Falha ao carregar desktop auto-salvo", {
      error: error.message,
    });
    return false;
  }
};

/**
 * Clear the auto-saved workspace from localStorage
 * @param {string} user - Username/email for scoping
 * @param {string} broker - Broker identifier for scoping
 */
export const clearAutoSavedWorkspace = (user, broker) => {
  try {
    const autoSaveKey = getAutoSaveKey(user, broker);
    localStorage.removeItem(autoSaveKey);
    console.log(
      `Auto-saved desktop cleared from localStorage [${autoSaveKey}]`,
    );
  } catch (error) {
    console.error("Failed to clear auto-saved desktop:", error);
  }
};
